import { Link } from "react-router-dom";
import { useState } from "react";
import { Check, MapPin, Calendar, Clock, ShieldCheck, Wrench, Sparkles, LogIn, ArrowRight, GraduationCap } from "lucide-react";
import { useEvents, useStores } from "@/lib/data-hooks";
import { AccessIcon } from "@/components/AccessIcon";
import { JoinFlowModal } from "@/components/JoinFlowModal";
import event1 from "@/assets/event-1.jpg";
import event2 from "@/assets/event-2.jpg";
import event3 from "@/assets/event-3.jpg";
import event4 from "@/assets/event-4.jpg";
import storeEmporia from "@/assets/store-emporia.webp";
import storeTriangeln from "@/assets/store-triangeln.webp";
import storeMobilia from "@/assets/store-mobilia.webp";
import storeKronprinsen from "@/assets/store-kronprinsen.webp";

const eventImages = [event1, event2, event3, event4];
const trainingImage = event2;

const META_TITLE = "Lagamobilen Access — Personlig teknikhjälp när du behöver den";
const META_DESC = "Som Access-medlem får du personlig hjälp av riktiga tekniker, prioriterad service och medlemsförmåner i alla våra butiker i Malmö. 99 kr/mån inkl. moms, minsta löptid 3 månader.";

const STORES = [
  { name: "Emporia", street: "Hyllie Boulevard 19", city: "Malmö", desc: "I gallerian Emporia — nära parkering och tågstation.", image: storeEmporia, mapUrl: "https://goo.gl/maps/oMSsnRnVTtLzdmCAA" },
  { name: "Triangeln", street: "Södra Förstadsgatan 41", city: "Malmö", desc: "I shoppingcentret Triangeln — mitt i centrala Malmö.", image: storeTriangeln, mapUrl: "https://goo.gl/maps/YzVktmU7z12dEQxM7" },
  { name: "Mobilia", street: "Per Albin Hanssons väg 36", city: "Malmö", desc: "I gallerian Mobilia — lätt att nå med bil eller buss.", image: storeMobilia, mapUrl: "https://goo.gl/maps/nMv4RKbMZdv5vM5H8" },
  { name: "Kronprinsen", street: "Mariedalsvägen 24", city: "Malmö", desc: "I Kronprinsen-gallerian — nära Slottsstaden och Limhamn.", image: storeKronprinsen, mapUrl: "https://www.google.com/maps/search/?api=1&query=Elon+Ljud+%26+Bild+Kronprinsen+Malm%C3%B6" },
];

function AccessPublicPage() {
  const { data: dbEvents = [] } = useEvents({ onlyActive: true, upcomingOnly: true });
  const { data: dbStores = [] } = useStores();
  const storeMap = new Map(dbStores.map(s => [s.id, s.name]));
  const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAJ", "JUN", "JUL", "AUG", "SEP", "OKT", "NOV", "DEC"];
  const events = dbEvents.slice(0, 4).map(e => {
    const d = new Date(e.starts_at);
    return {
      id: e.id,
      day: String(d.getDate()).padStart(2, "0"),
      month: MONTHS[d.getMonth()],
      title: e.title,
      store: e.store_id ? storeMap.get(e.store_id) ?? "—" : "—",
      time: d.toLocaleString("sv-SE", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }),
      spots: e.max_attendees,
    };
  });
  const [showJoinModal, setShowJoinModal] = useState(false);

  function openJoin() {
    setShowJoinModal(true);
  }

  return (
    <div className="bg-[var(--color-soft)]">
      <main className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-8 sm:py-12">

        {/* HERO — fokus på personlig teknikhjälp */}
        <section className="grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[var(--color-primary)] font-display font-semibold text-[11px] uppercase tracking-[0.18em]">
              <Sparkles size={12} /> Lagamobilen Access
            </span>
            <h1 className="!text-[2rem] sm:!text-4xl lg:!text-[2.7rem] !leading-[1.1] mt-3 text-foreground font-display font-bold">
              Personlig teknikhjälp <span className="text-[var(--color-gold)]">när du behöver den.</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Som Access-medlem får du en egen teknikpartner i Malmö — riktiga tekniker, prioriterad service och förmåner i alla våra butiker.
            </p>

            <ul className="mt-6 space-y-2.5 max-w-xl">
              {[
                "Kostnadsfri personlig hjälp av tekniker",
                "Prioriterad service",
                "Fri felsökning",
                "20% rabatt på reparationer",
                "50% rabatt på tillbehör",
              ].map(t => (
                <li key={t} className="flex items-start gap-3 text-[15px] text-foreground">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[var(--color-primary)]/12 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[var(--color-primary)]" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Prisruta + CTA */}
          <div className="access-membership-card rounded-3xl bg-[var(--color-ink)] text-white p-7 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[var(--color-gold)] opacity-[0.08] blur-3xl" />
            <AccessIcon size={40} className="absolute top-6 right-6 text-[var(--color-gold)] opacity-80" title="Lagamobilen Access" />
            <div className="relative">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--color-gold)] font-display font-semibold">Medlemskap</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-bold text-[var(--color-gold)] text-5xl leading-none">99 kr</span>
                <span className="text-white/70 text-sm">/mån inkl. moms</span>
              </div>
              <div className="text-white/55 text-xs mt-2">Minsta löptid 3 månader · Därefter månadsvis</div>

              <button
                type="button"
                onClick={openJoin}
                className="mt-6 w-full inline-flex items-center justify-center py-3 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition text-sm font-display font-semibold tracking-wide"
              >
                Bli medlem
              </button>
              <Link
                to="/access/login"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full border border-white/20 text-white/85 hover:bg-white/5 transition text-sm font-display tracking-wide"
              >
                <LogIn size={14} /> Logga in
              </Link>

              <div className="mt-6 pt-5 border-t border-white/10 text-[12px] text-white/60 leading-relaxed">
                Vi hjälper dig med registrering i någon av våra fyra butiker i Malmö.
                <span className="block mt-1 text-white/50">Elon Ljud & Bild</span>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST — kompakt rad */}
        <section className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Clock, headline: "45 Min", sub: "reparationstid" },
            { icon: Wrench, headline: "25 000+", sub: "utförda reparationer" },
            { icon: ShieldCheck, headline: "12 månader", sub: "garanti" },
            { icon: MapPin, headline: "4 butiker", sub: "i Malmö" },
          ].map(({ icon: Icon, headline, sub }) => (
            <div key={headline} className="bg-white rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2">
                <Icon size={18} className="text-[var(--color-primary)] shrink-0" />
                <div className="text-sm font-display font-bold text-foreground leading-none">{headline}</div>
              </div>
              <div className="text-[11px] text-muted-foreground leading-snug mt-1.5">{sub}</div>
            </div>
          ))}
        </section>


        {/* PERSONLIG TEKNIKHJÄLP — featured image med kommande utbildningar */}
        <section className="mt-10 sm:mt-14">
          <div className="relative rounded-3xl overflow-hidden bg-[var(--color-ink)] min-h-[460px] lg:min-h-[560px]">
            <img
              src={trainingImage}
              alt="Tekniker från Lagamobilen håller utbildning för Access-medlemmar i butik"
              className="absolute inset-0 w-full h-full object-cover object-center"
              loading="lazy"
            />
            {/* Läsbarhet bakom text till vänster — låter bilden andas till höger */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent lg:via-black/25" />
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/55 to-transparent" />

            <div className="relative p-6 sm:p-10 lg:p-14 grid lg:grid-cols-[1fr_minmax(0,420px)] gap-8 lg:gap-12 items-start text-white">
              {/* Vänster: budskap */}
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 text-[var(--color-gold)] text-[11px] uppercase tracking-[0.18em] font-display font-semibold">
                  <GraduationCap size={14} /> Utbildning ingår
                </span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-[2.2rem] leading-[1.15] mt-3">
                  Lär dig av <span className="text-[var(--color-gold)]">riktiga tekniker.</span>
                </h2>
                <p className="text-white/85 text-sm sm:text-base mt-4 leading-relaxed">
                  Våra medlemskvällar är utbildningar i butik — där du lär dig hantera din telefon, dator och smarta hem av en tekniker. Inställningar, säkerhet, backup, nya funktioner och allt du undrar över. Inkluderat i medlemskapet.
                </p>
                <button
                  type="button"
                  onClick={openJoin}
                  className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] transition text-sm font-display font-semibold tracking-wide"
                >
                  Bli medlem <ArrowRight size={14} />
                </button>
              </div>

              {/* Höger: kommande utbildningar – små rutor utan bilder */}
              <div>
                <div className="flex items-center gap-2 text-white/75 text-[10px] uppercase tracking-[0.18em] font-display font-semibold mb-3">
                  <Calendar size={12} className="text-[var(--color-gold)]" /> Kommande utbildningar
                </div>
                <ul className="space-y-2">
                  {events.slice(0, 4).map((e) => {
                    const urgent = e.spots <= 20;
                    return (
                      <li
                        key={e.id}
                        className="flex items-center gap-3 rounded-xl bg-white/[0.08] backdrop-blur-sm border border-white/15 px-3 py-2.5 hover:bg-white/[0.14] transition"
                      >
                        <div className="shrink-0 w-11 text-center rounded-md bg-white text-[var(--color-ink)] py-1">
                          <div className="font-display font-bold text-base leading-none">{e.day}</div>
                          <div className="text-[8px] tracking-widest text-muted-foreground mt-0.5">{e.month}</div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-semibold text-[13px] leading-snug truncate">{e.title}</div>
                          <div className="text-[11px] text-white/65 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1"><MapPin size={10} className="text-[var(--color-gold)]" />{e.store}</span>
                            <span className="text-white/30">·</span>
                            <span>{e.time}</span>
                          </div>
                        </div>
                        {urgent && (
                          <span className="shrink-0 bg-[var(--color-gold)] text-[var(--color-ink)] rounded-full px-2 py-0.5 text-[9px] font-display font-bold uppercase tracking-wider">
                            {e.spots} kvar
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="text-white/55 text-[11px] mt-3 tracking-wide">
                  Endast för Access-medlemmar. Anmälan i butik.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VAD INGÅR I ACCESS */}
        <section className="mt-14 sm:mt-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[var(--color-gold)] text-[11px] uppercase tracking-[0.18em] font-display font-semibold">Medlemskapet</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-2">Vad ingår i Access?</h2>
            <p className="text-muted-foreground text-sm mt-3 leading-relaxed">
              Allt du behöver för att tekniken ska fungera — och en riktig tekniker att vända dig till när den inte gör det.
            </p>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {/* Alltid aktivt */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={18} className="text-[var(--color-primary)]" />
                <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--color-gold)] font-display font-semibold">Alltid aktivt</div>
              </div>
              <ul className="space-y-2.5 text-sm text-foreground">
                {["20% rabatt på reparationer", "Fri felsökning", "Prioriterad service", "VIP-erbjudanden", "Partnerförmåner"].map(t => (
                  <li key={t} className="flex items-start gap-2"><Check size={14} className="text-[var(--color-primary)] mt-0.5 shrink-0" />{t}</li>
                ))}
              </ul>
            </div>

            {/* Varje månad — utmärks lätt */}
            <div className="bg-white rounded-2xl border-2 border-[var(--color-primary)]/25 p-6 relative">
              <span className="absolute -top-3 left-6 bg-[var(--color-primary)] text-white text-[10px] uppercase tracking-widest font-display font-bold px-2.5 py-1 rounded-full">Mest värde</span>
              <div className="flex items-center gap-2 mb-4">
                <Wrench size={18} className="text-[var(--color-primary)]" />
                <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--color-gold)] font-display font-semibold">Varje månad</div>
              </div>
              <ul className="space-y-2.5 text-sm text-foreground">
                {[
                  "Kostnadsfri personlig hjälp av tekniker",
                  "Fri rensning av telefon eller dator",
                  "50% rabatt på tillbehör",
                ].map(t => (
                  <li key={t} className="flex items-start gap-2"><Check size={14} className="text-[var(--color-primary)] mt-0.5 shrink-0" />{t}</li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-4 leading-relaxed border-t border-border pt-3">
                Hjälp med inställningar, säkerhet, backup, överföring av innehåll och felsökning — av en riktig tekniker.
              </p>
            </div>

            {/* Exklusivt */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-[var(--color-primary)]" />
                <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--color-gold)] font-display font-semibold">Exklusivt</div>
              </div>
              <ul className="space-y-2.5 text-sm text-foreground">
                {["Utbildningskvällar med tekniker", "Produktvisningar & nyheter", "Specialerbjudanden", "Solid Försäkring till medlemspris"].map(t => (
                  <li key={t} className="flex items-start gap-2"><Check size={14} className="text-[var(--color-primary)] mt-0.5 shrink-0" />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>


        {/* VÅRA BUTIKER */}
        <section className="mt-14 sm:mt-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[var(--color-gold)] text-[11px] uppercase tracking-[0.18em] font-display font-semibold">Lokal närvaro</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-2">Elon Ljud & Bild</h2>
            <p className="text-muted-foreground text-sm mt-3 leading-relaxed">
              Fyra butiker i Malmö — alltid en nära dig. Som medlem får du samma personliga service i alla våra Elon Ljud & Bild-butiker.
            </p>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STORES.map((s) => (
              <div key={s.name} className="bg-white rounded-2xl border border-border overflow-hidden flex flex-col">
                <div className="relative h-32">
                  <img src={s.image} alt={`Lagamobilen ${s.name} i Malmö`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  {/* Toning — mörk botten, lila i hörn för premiumkänsla */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-ink)]/90 via-[var(--color-ink)]/30 to-transparent" />
                  <div className="absolute inset-0 opacity-40 mix-blend-multiply" style={{ background: "radial-gradient(circle at 80% 0%, color-mix(in oklab, var(--color-primary) 70%, transparent) 0%, transparent 55%)" }} />
                  <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-2">
                    <div>
                      <div className="text-[10px] text-white/70 uppercase tracking-wider font-display font-semibold">Elon Ljud & Bild</div>
                      <div className="font-display font-semibold text-white text-lg tracking-wide drop-shadow leading-tight">{s.name}</div>
                    </div>
                    <div className="text-[var(--color-gold)] text-[10px] uppercase tracking-[0.18em] font-display font-semibold drop-shadow">Malmö</div>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <MapPin size={12} className="text-[var(--color-primary)] mt-0.5 shrink-0" />
                    <div>
                      <div>{s.street}</div>
                      <div>{s.city}</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed flex-1">{s.desc}</p>
                  <a
                    href={s.mapUrl}
                    target="_blank" rel="noreferrer"
                    className="mt-3 inline-flex items-center justify-center text-xs font-display font-semibold text-[var(--color-primary)] hover:underline"
                  >
                    Hitta hit →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* BLI MEDLEM — CTA */}
        <section id="bli-medlem" className="mt-14 sm:mt-20 scroll-mt-8">
          <div className="rounded-3xl bg-[var(--color-ink)] text-white p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[var(--color-gold)] opacity-[0.08] blur-3xl" />
            <div className="relative max-w-xl mx-auto">
              <span className="text-[var(--color-gold)] text-[11px] uppercase tracking-[0.18em] font-display font-semibold">Bli medlem</span>
              <h2 className="font-display font-bold text-3xl sm:text-4xl mt-2">Teckna ditt medlemskap online</h2>
              <p className="text-white/75 text-sm sm:text-base mt-3 leading-relaxed">
                Skapa konto och betala 99 kr/mån (inkl. moms) direkt — eller besök någon av våra fyra butiker i Malmö.
              </p>
              <button
                type="button"
                onClick={openJoin}
                className="mt-6 inline-flex items-center justify-center px-8 py-3 rounded-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white font-display font-semibold text-sm tracking-wide transition"
              >
                Bli medlem nu
              </button>
              <p className="text-white/55 text-xs mt-4">
                Kortuppgifter hanteras säkert av Stripe. Minsta löptid 3 månader, därefter månadsvis tills du säger upp.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-14 sm:mt-20">
          <h2 className="font-display font-bold text-2xl sm:text-3xl mb-5">Vanliga frågor</h2>
          <div className="space-y-3 max-w-3xl">
            {[
              { q: "Vad kostar Lagamobilen Access?", a: "99 kr per månad inklusive moms. Medlemskapet har en minsta löptid om 3 månader, därefter löper det månadsvis tills du säger upp." },
              { q: "Hur blir jag medlem?", a: "Teckna direkt online via knappen ovan, eller besök någon av våra fyra Elon Ljud & Bild-butiker i Malmö." },
              { q: "Vad menas med personlig teknikhjälp?", a: "Varje månad kan du få kostnadsfri hjälp av en riktig tekniker i någon av våra butiker." },
              { q: "Var gäller förmånerna?", a: "Alla förmåner gäller i samtliga Lagamobilen-butiker i Malmö. Partnerrabatter gäller hos respektive partner." },
              { q: "Kan jag avsluta mitt medlemskap?", a: "Ja, du säger upp direkt i din medlemsportal. De första 3 månaderna är en minsta löptid — säger du upp tidigare avslutas medlemskapet automatiskt när bindningstiden löpt ut. Därefter avslutas det vid innevarande periods slut." },
            ].map(f => (
              <details key={f.q} className="bg-white rounded-2xl border border-border p-5 group">
                <summary className="font-display font-bold cursor-pointer list-none flex justify-between items-center text-sm">
                  {f.q}
                  <span className="text-[var(--color-primary)] group-open:rotate-45 transition text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <p className="text-center text-xs text-muted-foreground mt-14">
          Är du butikspersonal? <Link to="/af-portal" className="text-[var(--color-primary)] hover:underline">Gå till ÅF-portalen</Link>
        </p>
      </main>

      <JoinFlowModal open={showJoinModal} onClose={() => setShowJoinModal(false)} />
    </div>
  );
}

export default AccessPublicPage;
